import React, { useId } from 'react';

// Glyph paths from the ToDesktop app icon (td-new-logo.svg), drawn without the
// tile. The ribbon (top cap, crossbar, bowl and fold) sits over the stem; the
// stem pieces are cut where the ribbon passes over them and the cuts are
// filled with a lighter "inlay" tint so the mark stays one solid shape.
const FULL =
  'M169.126 52.9514C207.79 52.9514 239.58 82.3791 243.321 120.058H288.621C308.274 120.058 325.229 120.057 339.193 121.198C353.932 122.402 368.592 125.061 382.699 132.249C403.744 142.971 420.854 160.082 431.577 181.126C438.764 195.233 441.423 209.893 442.627 224.632C443.768 238.596 443.768 255.549 443.767 275.2V289.059C443.767 305.961 443.768 320.545 442.918 332.606C442.022 345.31 440.052 358.006 434.684 370.499C423.375 396.823 402.395 417.802 376.072 429.111C363.579 434.479 350.883 436.45 338.179 437.345C329.503 437.956 319.521 438.128 308.311 438.176L298.895 438.194C298.501 438.194 298.115 438.194 297.736 438.194C296.71 438.194 295.675 438.194 294.631 438.194H293.398V438.184C286.646 438.145 282.593 437.943 278.781 436.92C274.574 435.791 270.598 433.933 267.032 431.431C263.008 428.607 259.721 424.679 253.147 416.823L196.931 349.638C186.634 337.331 181.485 331.178 177.82 324.269C174.569 318.14 172.189 311.587 170.749 304.8C170.495 303.602 170.281 302.394 170.1 301.149C169.459 297.216 169.126 293.181 169.126 289.068V194.621H151.728C110.548 194.621 77.165 161.238 77.165 120.058L169.126 120.058V52.9514ZM243.689 363.631H293.398C311.88 363.631 323.812 363.609 332.936 362.966C341.71 362.348 345.038 361.291 346.639 360.603C355.413 356.834 362.407 349.84 366.176 341.066C366.864 339.465 367.921 336.138 368.539 327.364C369.182 318.239 369.204 306.307 369.204 287.825V276.641C369.204 255.146 369.175 241.267 368.312 230.704C367.484 220.576 366.08 216.821 365.14 214.977C361.566 207.962 355.863 202.259 348.848 198.685C347.004 197.746 343.249 196.341 333.121 195.513C322.558 194.65 308.679 194.621 287.184 194.621H243.689V363.631Z';
const STEM_TOP =
  'M169.126 52.9514C207.79 52.9514 239.58 82.3791 243.321 120.058L169.126 120.058V52.9514Z';
const STEM_LOWER =
  'M169.126 194.621V273.08C169.126 286.613 169.126 294.439 170.1 301.149C175.873 336.584 206.622 363.631 243.689 363.631V194.621H169.126Z';
const RIBBON =
  'M243.321 120.058L77.165 120.058C77.165 161.238 110.548 194.621 151.728 194.621H287.184C308.679 194.621 322.558 194.65 333.121 195.513C343.249 196.341 347.004 197.746 348.848 198.685C355.863 202.259 361.566 207.962 365.14 214.977C366.08 216.821 367.484 220.576 368.312 230.704C369.175 241.267 369.204 255.146 369.204 276.641V287.825C369.204 306.307 369.182 318.239 368.539 327.364C367.921 336.138 366.864 339.465 366.176 341.066C362.407 349.84 355.413 356.834 346.639 360.603C345.038 361.291 341.71 362.348 332.936 362.966C323.812 363.609 311.88 363.631 293.398 363.631H243.689C206.622 363.631 175.873 336.584 170.1 301.149C170.281 302.394 170.495 303.602 170.749 304.8C172.189 311.587 174.569 318.14 177.82 324.269C181.485 331.178 186.634 337.331 196.931 349.638L253.147 416.823C259.721 424.679 263.008 428.607 267.032 431.431C270.598 433.933 274.574 435.791 278.781 436.92C282.593 437.943 286.646 438.145 293.398 438.184V438.194H297.736L308.311 438.194V438.176C319.521 438.128 329.503 437.956 338.179 437.345C350.883 436.45 363.579 434.479 376.072 429.111C402.395 417.802 423.375 396.823 434.684 370.499C440.052 358.006 442.022 345.31 442.918 332.606C443.768 320.545 443.767 305.961 443.767 289.059V275.199C443.768 255.548 443.768 238.595 442.627 224.632C441.423 209.893 438.764 195.233 431.577 181.126C420.854 160.082 403.744 142.971 382.699 132.249C368.592 125.061 353.932 122.402 339.193 121.198C325.229 120.057 308.274 120.058 288.621 120.058H243.321Z';

const VIEW_BOX = { x: 62, y: 38, width: 397, height: 415 };

interface ToDesktopMarkProps {
  /** Rendered height in px; width follows the glyph's aspect ratio. */
  size?: number;
  /** Accessible name. Omit when the mark sits next to visible text. */
  title?: string;
}

export const ToDesktopMark: React.FC<ToDesktopMarkProps> = ({
  size = 24,
  title,
}) => {
  const maskId = `td-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const width = Math.round((size * VIEW_BOX.width) / VIEW_BOX.height);

  return (
    <svg
      viewBox={`${VIEW_BOX.x} ${VIEW_BOX.y} ${VIEW_BOX.width} ${VIEW_BOX.height}`}
      width={width}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="552"
          height="558"
        >
          <rect width="552" height="558" fill="#fff" />
          <path
            d={RIBBON}
            fill="#000"
            stroke="#000"
            strokeWidth={26}
            strokeLinejoin="round"
          />
        </mask>
      </defs>
      <path
        d={FULL}
        fillRule="evenodd"
        style={{ fill: 'var(--logo-inlay, #c3c8ff)' }}
      />
      <g style={{ fill: 'var(--logo-fill, #3946f1)' }}>
        <g mask={`url(#${maskId})`}>
          <path d={STEM_TOP} />
          <path d={STEM_LOWER} />
        </g>
        <path d={RIBBON} />
      </g>
    </svg>
  );
};

export default ToDesktopMark;
